import './LeftRightGroupableSelect.less';
import React, {useState, useEffect} from 'react';
import {translate} from "../../i18n";
import Icon from "../icon/Icon";


export default function LeftRightGroupableSelect(
    {
        options = [],
        groups = [],
        defaultValue = [],
        value: forcedValue = undefined,
        onChange = value => {},
        title = translate('All options'),
        renderSelectedSummary = selectedCount => translate('Selected (::count)', {count: selectedCount}),
        renderDeselectAllText = selectedCount => translate('Deselect all'),
        renderGroupTitle = group => group.name,
        renderOptionContent = (option, extras) => option.name,
        getGroupId = group => group.id,
        getOptionId = option => option.id,
        getOptionDisabled = (option, extras) => option.disabled,
        getOptionGroupId = option => option.groupId,
        disabled: allDisabled = false,
    }
) {

    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        onChange(value);
    }, [value]);

    useEffect(() => {
        if (forcedValue !== undefined) {
            setValue(forcedValue);
        }
    }, [forcedValue]);

    const select = (optionId) => {
        if (!value.includes(optionId)) {
            setValue(value.concat([optionId]));
        }
    };

    const deselect = (optionId) => {
        if (value.includes(optionId)) {
            setValue(value.filter(v => v !== optionId));
        }
    };

    const deselectAll = () => {
        const keptOptions = getSelectedOptions().filter(option => {
            const group = getOptionGroup(option);
            const extras = createOptionExtras(true, group, false);
            return getOptionDisabled(option, extras);
        });

        setValue(keptOptions.map(option => getOptionId(option)));
    };

    const createOptionExtras = (selected, group, inLeftColumn) => {
        return {selected, group, inLeftColumn};
    };

    const renderOption = (option, group, inLeftColumn) => {
        const optionId = getOptionId(option);
        const selected = value.includes(optionId);
        const extras = createOptionExtras(selected, group, inLeftColumn);
        const disabled = allDisabled || getOptionDisabled(option, extras);
        const selectable = inLeftColumn && !disabled;

        const grouped = group && inLeftColumn;
        const highlighted = selectable && selected && inLeftColumn;

        return <li
            key={optionId}
            onClick={inLeftColumn && !disabled ? () => select(optionId) : undefined}
            className={[
                selectable && 'selectable',
                selected && 'selected',
                disabled && 'disabled',
                grouped && 'grouped',
                highlighted && 'highlighted',
            ].filter(Boolean).join(' ')}
        >
            <div className="option">
                {renderOptionContent(option, extras)}
            </div>
            {
                !disabled &&
                <button
                    type="button"
                    onClick={!inLeftColumn && !disabled ? () => deselect(optionId) : undefined}
                    className={inLeftColumn ? 'select-button' : 'deselect-button'}
                >
                    {
                        inLeftColumn
                            ? <Icon name="arrow-right"/>
                            : <Icon name="times"/>
                    }
                </button>
            }
        </li>;
    };

    const renderGroup = (group, index) => {
        const groupOptions = options.filter(option => getOptionGroupId(option) === getGroupId(group));
        const defaultCollapsed = groups.length > 0;

        return <Group
            key={getGroupId(group)}
            titleComponent={renderGroupTitle(group)}
            optionComponents={groupOptions.map(option => renderOption(option, group, true))}
            defaultCollapsed={defaultCollapsed}
        />;
    };

    const getSelectedOptions = () => {
        return value.map(optionId => options.find(option => getOptionId(option) === optionId)).filter(Boolean);
    };

    const getOptionGroup = (option) => {
        return groups.find(group => getGroupId(group) === getOptionGroupId(option));
    };

    return <div className="left-right-groupable-select">
        <table>
            <thead>
            <tr>
                <td className="left">
                    <div className="inner">
                        <div className="title">{title}</div>
                    </div>
                </td>
                <td className="right">
                    <div className="inner">
                        <div className="selected-summary">
                            {renderSelectedSummary(value.length)}
                        </div>
                        {
                            !allDisabled &&
                            value.length > 0 &&
                            <button
                                type="button"
                                className="deselect-all-button"
                                onClick={() => deselectAll()}
                            >
                                {renderDeselectAllText(value.length)}
                            </button>
                        }
                    </div>
                </td>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td className="left">
                    <div className="inner">
                        {groups.map((group, index) => renderGroup(group, index))}
                    </div>
                </td>
                <td className="right">
                    <div className="inner">
                        <ul>
                            {getSelectedOptions().map(option => renderOption(option, getOptionGroup(option), false))}
                        </ul>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>
    </div>;
}


function Group({titleComponent, optionComponents, defaultCollapsed}) {

    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    return <div className="group">
        <div
            className="group-heading"
            onClick={() => setCollapsed(t => !t)}
        >
            {
                collapsed
                    ?
                    <Icon name="angle-right"/>
                    :
                    <Icon name="angle-down"/>
            }
            <div className="title">{titleComponent}</div>
        </div>
        {
            collapsed ||
            <div className="group-body">
                <ul>
                    {optionComponents}
                </ul>
            </div>
        }
    </div>;
}

